import {
    FilterChipSelect,
    FilterOption,
    FilterWithoutDividerOption,
} from '@/components/Voice/VoiceModal/SelectVoiceContent/FiltersSection/FilterChipSelect';
import FacebookIcon from '@/images/Facebook.svg';
import InstagramIcon from '@/images/Instagram.svg';
import LandscapeIcon from '@/images/Landscape.svg';
import PortraitIcon from '@/images/Portrait.svg';
import SquareIcon from '@/images/Square.svg';
import TikTokIcon from '@/images/Tik-Tok.svg';
import YoutubeShortIcon from '@/images/Youtube-Shorts.svg';
import YoutubeIcon from '@/images/Youtube.svg';
import AltArrowDown from '@/images/arrows/AltArrowDown.svg';
import styled from '@emotion/styled';
import { SelectChangeEvent } from '@mui/material/Select';
import { MaskedIcon } from '../MaskedIcon/MaskedIcon';

const SidebarSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;

    .MuiTabs-root {
        min-height: unset;
    }

    .MuiTabs-flexContainer {
        gap: 8px;
        flex-wrap: wrap;
    }
`;

export const SectionTitle = styled.h3`
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.black[700]};
    margin: 0;
`;

const IconWrapper = styled.img`
    padding: 2px;
    width: 24px !important;
    height: 24px !important;
    box-sizing: content-box;
`;

const StyledFilterChipSelect = styled(FilterChipSelect)`
    .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input {
        padding-right: 24px;
    }
`;

const ExpandMoreIcon = (props: any) => {
    return <MaskedIcon src={AltArrowDown} size="24px" {...props} style={{ pointerEvents: 'none', top: 'calc(50% - 10px)' }} />;
};

export enum AspectRatio {
    Youtube = 'youtube',
    YoutubeShort = 'youtube-short',
    Facebook = 'facebook',
    Instagram = 'instagram',
    TikTok = 'tiktok',
    Landscape = 'landscape',
    Portrait = 'portrait',
    Square = 'square',
}

export const aspectRatioOptions: FilterOption[] = [
    { value: 'all', label: 'All', chipLabel: 'All' },
    {
        value: AspectRatio.Youtube,
        label: 'Youtube Video',
        secondaryLabel: '(16:9)',
        chipLabel: 'Youtube Video',
        icon: <IconWrapper src={YoutubeIcon} alt="Youtube" />,
    },
    {
        value: AspectRatio.YoutubeShort,
        label: 'Youtube Short',
        secondaryLabel: '(9:16)',
        chipLabel: 'Youtube Short',
        icon: <IconWrapper src={YoutubeShortIcon} alt="Youtube Short" />,
    },
    {
        value: AspectRatio.Facebook,
        label: 'Facebook Video',
        secondaryLabel: '(1:1)',
        chipLabel: 'Facebook Video',
        icon: <IconWrapper src={FacebookIcon} alt="Facebook" />,
    },
    {
        value: AspectRatio.Instagram,
        label: 'Instagram Reel',
        secondaryLabel: '(9:16)',
        chipLabel: 'Instagram Reel',
        icon: <IconWrapper src={InstagramIcon} alt="Instagram" />,
    },
    {
        value: AspectRatio.TikTok,
        label: 'TikTok Video',
        secondaryLabel: '(9:16)',
        chipLabel: 'TikTok Video',
        icon: <IconWrapper src={TikTokIcon} alt="TikTok" />,
    },
    { isDivider: true, value: 'divider' },
    {
        value: AspectRatio.Landscape,
        label: 'Landscape',
        secondaryLabel: '(16:9)',
        chipLabel: 'Landscape',
        icon: <IconWrapper src={LandscapeIcon} alt="Landscape" />,
    },
    {
        value: AspectRatio.Portrait,
        label: 'Portrait',
        secondaryLabel: '(9:16)',
        chipLabel: 'Portrait',
        icon: <IconWrapper src={PortraitIcon} alt="Portrait" />,
    },
    {
        value: AspectRatio.Square,
        label: 'Square',
        secondaryLabel: '(1:1)',
        chipLabel: 'Square',
        icon: <IconWrapper src={SquareIcon} alt="Square" />,
    },
];

export type AspectRatioValue = (typeof aspectRatioOptions)[number]['value'];

interface AspectRatioFilterProps {
    selectedAspectRatio: FilterOption;
    onAspectRatioChange: (e: SelectChangeEvent<string>) => void;
    onOpen?: () => void;
    showAll?: boolean;
    disabled?: boolean;
    title?: string;
    transformValue?: (value: FilterWithoutDividerOption) => FilterOption;
    className?: string;
}

export const AspectRatioDropdown = ({
    selectedAspectRatio,
    onAspectRatioChange,
    onOpen,
    showAll = true,
    disabled = false,
    title,
    transformValue,
    className,
}: AspectRatioFilterProps) => {
    const options = showAll ? aspectRatioOptions : aspectRatioOptions.filter(option => option.value !== 'all');

    return (
        <SidebarSection className={className}>
            {title && <SectionTitle>{title}</SectionTitle>}
            <div>
                <StyledFilterChipSelect
                    data-testid="aspect-ratio-select"
                    id="aspect-ratio-select"
                    value={selectedAspectRatio}
                    transformValue={transformValue}
                    options={options}
                    onChange={onAspectRatioChange}
                    onOpen={onOpen}
                    variant="outlined"
                    disabled={disabled}
                    IconComponent={ExpandMoreIcon}
                    menuProps={{
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: -10,
                            horizontal: 0,
                        },
                    }}
                />
            </div>
        </SidebarSection>
    );
};
